import { BASE_URL_API, BASIC_AUTH } from "services/Http";

const MENU_DEBUG_KEY = "__envy_menu_debug_v1__";
const MENU_DEBUG_MAX_ENTRIES = 250;
const TARGET_DEBUG_EMAIL = "sandra.palacios@smartdealer.com";
const MENU_DEBUG_ENDPOINT = `${BASE_URL_API}/debug/menu-events`;

const isBrowser = typeof window !== "undefined";

function normalizeEmail(value = "") {
    return String(value || "").trim().toLowerCase();
}

function shouldLogForUser(payload = {}) {
    const userEmail = normalizeEmail(payload.userEmail);
    return userEmail === TARGET_DEBUG_EMAIL;
}

function getRequestMeta() {
    if (!isBrowser) {
        return {
            dealerId: 0,
            userId: 0,
            userName: "",
        };
    }

    try {
        const rawUser = window.localStorage.getItem("_gid");
        let parsedUser = {};

        if (rawUser) {
            try {
                parsedUser = JSON.parse(window.atob(rawUser));
            } catch (decodeError) {
                try {
                    parsedUser = JSON.parse(rawUser);
                } catch (parseError) {
                    parsedUser = {};
                }
            }
        }

        return {
            dealerId: parseInt(window.localStorage.getItem("_dlid") || "0", 10) || 0,
            userId: parsedUser?.id || 0,
            userName: `${parsedUser?.firstName || ""} ${parsedUser?.lastName || ""}`.trim(),
        };
    } catch (error) {
        return {
            dealerId: 0,
            userId: 0,
            userName: "",
        };
    }
}

function readState() {
    if (!isBrowser) {
        return { queue: [], sent: [] };
    }

    try {
        const raw = window.localStorage.getItem(MENU_DEBUG_KEY);
        if (!raw) return { queue: [], sent: [] };

        const parsed = JSON.parse(raw);
        return {
            queue: Array.isArray(parsed?.queue) ? parsed.queue : [],
            sent: Array.isArray(parsed?.sent) ? parsed.sent : [],
        };
    } catch (error) {
        console.error("Failed to read menu debug state", error);
        return { queue: [], sent: [] };
    }
}

function writeState(nextState) {
    if (!isBrowser) return;

    try {
        const state = {
            queue: (nextState.queue || []).slice(-MENU_DEBUG_MAX_ENTRIES),
            sent: (nextState.sent || []).slice(-MENU_DEBUG_MAX_ENTRIES),
        };
        window.localStorage.setItem(MENU_DEBUG_KEY, JSON.stringify(state));
    } catch (error) {
        console.error("Failed to write menu debug state", error);
    }
}

function buildHeaders() {
    const { dealerId, userId, userName } = getRequestMeta();

    return {
        "Content-Type": "application/json",
        Authorization: `Basic ${BASIC_AUTH}`,
        DealerId: dealerId,
        UserId: userId,
        UserName: userName,
    };
}

async function sendEntries(entries, keepalive = false) {
    if (!entries.length) return true;

    const response = await fetch(MENU_DEBUG_ENDPOINT, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({ entries }),
        keepalive,
    });

    if (!response.ok) {
        throw new Error(`Menu debug endpoint failed with status ${response.status}`);
    }

    return true;
}

function syncHelpers(lastEntry = null) {
    if (!isBrowser || !window.__ENVY_MENU_DEBUG__) return;

    const state = readState();
    window.__ENVY_MENU_DEBUG__.lastEntry = lastEntry;
    window.__ENVY_MENU_DEBUG__.state = state;
}

let flushPromise = null;

export async function flushMenuDebugQueue(options = {}) {
    if (!isBrowser) return false;

    const { keepalive = false } = options;
    const state = readState();
    if (state.queue.length === 0) {
        syncHelpers();
        return true;
    }

    if (flushPromise) return flushPromise;

    flushPromise = sendEntries(state.queue, keepalive)
        .then(() => {
            const nextState = {
                queue: [],
                sent: [...state.sent, ...state.queue].slice(-MENU_DEBUG_MAX_ENTRIES),
            };
            writeState(nextState);
            syncHelpers(state.queue[state.queue.length - 1] || null);
            return true;
        })
        .catch((error) => {
            console.error("Failed to flush menu debug queue", error);
            syncHelpers();
            return false;
        })
        .finally(() => {
            flushPromise = null;
        });

    return flushPromise;
}

function enqueueEntry(entry) {
    const state = readState();
    const nextState = {
        queue: [...state.queue, entry].slice(-MENU_DEBUG_MAX_ENTRIES),
        sent: state.sent,
    };

    writeState(nextState);
    syncHelpers(entry);
}

export function logMenuDebug(action, payload = {}) {
    if (!isBrowser || !shouldLogForUser(payload)) return;

    const requestMeta = getRequestMeta();

    const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        ts: new Date().toISOString(),
        action,
        path: window.location.pathname,
        href: window.location.href,
        userAgent: window.navigator.userAgent,
        payload: {
            ...payload,
            dealerId: payload.dealerId || requestMeta.dealerId,
            userId: payload.userId || requestMeta.userId,
            userName: payload.userName || requestMeta.userName,
        },
    };

    enqueueEntry(entry);
    console.info("[envy-menu-debug]", entry);
    flushMenuDebugQueue();
}

export function installMenuDebugHelpers() {
    if (!isBrowser || window.__ENVY_MENU_DEBUG__) return;

    const flushOnExit = () => {
        flushMenuDebugQueue({ keepalive: true });
    };

    window.addEventListener("pagehide", flushOnExit);
    window.addEventListener("beforeunload", flushOnExit);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
            flushOnExit();
        }
    });

    window.__ENVY_MENU_DEBUG__ = {
        key: MENU_DEBUG_KEY,
        targetEmail: TARGET_DEBUG_EMAIL,
        endpoint: MENU_DEBUG_ENDPOINT,
        read: () => readState(),
        flush: () => flushMenuDebugQueue({ keepalive: true }),
        clear: () => {
            window.localStorage.removeItem(MENU_DEBUG_KEY);
            syncHelpers(null);
        },
        lastEntry: null,
        state: readState(),
    };
}
